import React from 'react';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CloudOffIcon from '@material-ui/icons/CloudOff';
import withShipment from '../../withShipment';
import { bonusPhotoQueueSelector } from '../../redux/selectors/settings';
import './PendingPhotoGate.css';

// Tells the employee a bonus photo is still on the phone.
//
// Until now the upload was completely silent: it succeeded, or it retried for
// a while and gave up, and either way the employee walked away believing the
// bonus was complete. The owner found out hours later from the server log.
// This is the one signal that lets the person holding the phone act — stay on
// wifi, keep the app open — while acting is still possible.
//
// Renders nothing when the outbox is empty, so it costs nothing on every other
// screen in the app.
const PendingPhotoBanner = ({ bonusPhotoQueue }) => {
  const count = bonusPhotoQueue?.count || 0;
  if (!count) return null;

  const stalled = Boolean(bonusPhotoQueue.stalled);
  const one = count === 1;
  const photoWord = one ? 'photo' : 'photos';

  // The count is what the employee OWES — this device's outbox unioned with
  // what the server is still missing — so some of it may not be on this phone
  // at all. Telling someone to "keep this app open" for a photo this device
  // isn't holding is advice they cannot act on, so the cases read differently.
  const notHere = !(bonusPhotoQueue.onThisDevice > 0);
  let message;
  if (notHere) {
    message = `bonus ${photoWord} never reached the server. ${
      one ? 'It was' : 'They were'
    } taken on another device — open NuVe there, or clock out and ${
      one ? 'it' : 'they'
    } will be recorded as missing.`;
  } else if (stalled) {
    message = `bonus ${photoWord} can’t reach the server. Stay on wifi and keep this app open — it keeps trying.`;
  } else {
    message = `bonus ${photoWord} still uploading. Keep this app open until ${
      one ? 'it finishes' : 'they finish'
    }.`;
  }

  // Real bytes-on-the-wire from axios, not a guess. Only shown while a photo
  // is genuinely in flight — a bar frozen at 40% says more than a spinner.
  const pct = bonusPhotoQueue.uploading ? bonusPhotoQueue.progress?.pct : null;

  return (
    <div
      role="status"
      className={`photo-banner ${
        stalled || notHere ? 'photo-banner--stalled' : 'photo-banner--uploading'
      }`}
    >
      <span className="photo-banner-icon" aria-hidden="true">
        {stalled || notHere ? (
          <CloudOffIcon fontSize="small" />
        ) : (
          <CloudUploadIcon fontSize="small" />
        )}
      </span>
      <span className="photo-banner-count">{count}</span>
      <span className="photo-banner-text">
        {message}
        {typeof pct === 'number' && (
          <strong className="photo-banner-pct"> {pct}%</strong>
        )}
      </span>
      {typeof pct === 'number' && (
        <span className="photo-banner-progress" aria-hidden="true">
          <span style={{ width: `${pct}%` }} />
        </span>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  bonusPhotoQueue: bonusPhotoQueueSelector(state),
});

export default withShipment({ mapStateToProps }, PendingPhotoBanner);
